import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { AutoResponderSelector } from "../../components/conversation/auto-responder-selector";
import { ConversationPreview } from "../../components/conversation/conversation-preview";
import {
  AIChatConfiguration,
  AIConfiguration,
} from "../../components/conversation/ai-chat-configuration";
import { LinkedInConversionConfiguration } from "../../components/linkedin/linkedin-conversion-configuration";
import { useAssets } from "../../hooks/use-assets";
import { useStartMessages } from "../../hooks/use-start-messages";
import { useDestinations } from "../../hooks/use-destinations";
import { useAutoResponderSearch } from "../../hooks/use-auto-responder-search";
import { useGlobalClient } from "../../hooks/use-global-client";
import {
  CREATE_CONVERSATION,
  UPDATE_CONVERSATION,
  LIST_CONVERSATIONS,
  GET_CONVERSATION,
  ATTACH_ASSETS_TO_CONVERSATION,
} from "../../services/graphql/conversation-operations";
import type { ContentSourceInput } from "../../generated/graphql";

// Utility function to clean GraphQL objects for mutations
const cleanContentSource = (
  contentSource: any
): ContentSourceInput | undefined => {
  if (!contentSource) return undefined;

  const cleaned: ContentSourceInput = {
    type: contentSource.type,
  };

  if (contentSource.destinationId) {
    cleaned.destinationId = contentSource.destinationId;
  }

  if (contentSource.fileId) {
    cleaned.fileId = contentSource.fileId;
  }

  if (contentSource.analysisStatus) {
    cleaned.analysisStatus = contentSource.analysisStatus;
  }

  return cleaned;
};

const cleanCalendlyIntegration = (calendlyIntegration: any) => {
  if (!calendlyIntegration) return undefined;

  // Only include fields that exist in the GraphQL schema
  return {
    enabled: calendlyIntegration.enabled,
    calendarLink: calendlyIntegration.calendarLink,
    connectionStatus: calendlyIntegration.connectionStatus,
    eventTypeId: calendlyIntegration.eventTypeId,
    bookingSettings: calendlyIntegration.bookingSettings,
  };
};

interface SelectedComponent {
  id: string;
  type: "asset" | "messageTemplate" | "autoResponder" | "destination";
  name: string;
  description?: string;
  assetType?: "pdf" | "image" | "video" | "url";
  templateType?: "SMS" | "CHAT" | "EMAIL" | "GENERAL";
  content?: string;
  url?: string;
}

export function CreateConversationPage() {
  // Check if we're in edit mode
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;

  // Remove unused client selection since it's handled in header
  const [conversationName, setConversationName] = useState("");
  const [conversationDescription, setConversationDescription] = useState("");
  const [selectedComponents, setSelectedComponents] = useState<
    SelectedComponent[]
  >([]);
  const [activeTab, setActiveTab] = useState<
    "asset" | "messageTemplate" | "autoResponder" | "destination"
  >("asset");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "available" | "added"
  >("all");
  const [selectedAutoResponderId, setSelectedAutoResponderId] = useState<
    string | null
  >(null);

  // AI Chat Configuration State
  const [aiConfiguration, setAiConfiguration] = useState<AIConfiguration>({
    aiChatEnabled: false,
    contentSource: undefined,
    calendlyIntegration: undefined,
    aiSettings: {
      model: "NOVA_LITE",
      temperature: 0.7,
      maxTokens: 1000,
    },
  });

  // Global client context - wait for client selection before making API calls
  const {
    selectedClient,
    loading: clientLoading,
    isSuperAdmin,
  } = useGlobalClient();
  // For super admins, allow fetching conversation data even without selected client (cross-tenant lookup)
  // For regular users, wait for client selection
  const shouldFetchData = isSuperAdmin
    ? !clientLoading
    : !!selectedClient && !clientLoading;

  // Debug query conditions
  const skipQuery = !isEditMode || !editId || !shouldFetchData;
  console.log("🔍 GET_CONVERSATION Query Debug:", {
    isEditMode,
    editId,
    shouldFetchData,
    isSuperAdmin,
    clientLoading,
    selectedClient: selectedClient?.id,
    skipQuery,
    skipReasons: {
      notEditMode: !isEditMode,
      noEditId: !editId,
      shouldNotFetchData: !shouldFetchData,
    },
  });

  // Navigation and mutations
  const navigate = useNavigate();

  // Fetch conversation data for editing
  const {
    data: conversationData,
    loading: conversationLoading,
    error: conversationError,
  } = useQuery(GET_CONVERSATION, {
    variables: { id: editId },
    skip: skipQuery,
    fetchPolicy: "cache-and-network", // Always fetch fresh data to get complete aiConfig
    onCompleted: (data) => {
      if (data?.getConversation) {
        const conversation = data.getConversation;
        console.log("🔄 Loading conversation for edit:", conversation);
        console.log("🤖 AI Config in conversation:", conversation.aiConfig);
        console.log("🤖 AI Config type:", typeof conversation.aiConfig);
        console.log(
          "🤖 AI Config keys:",
          conversation.aiConfig ? Object.keys(conversation.aiConfig) : "null"
        );

        setConversationName(conversation.name);
        setConversationDescription(conversation.description || "");
        setSelectedAssets(
          conversation.assets?.map((asset: any) => asset.fileId) || []
        );
        setSelectedAutoResponderId(conversation.autoResponderId || null);

        // Load AI configuration if present
        if (conversation.aiConfig) {
          console.log("🤖 Loading AI config from conversation:", conversation.aiConfig);

          // Transform old lowercase model values to new uppercase enum values
          const transformModel = (model: string): "NOVA_PRO" | "NOVA_LITE" => {
            switch (model) {
              case "nova-pro":
                return "NOVA_PRO";
              case "nova-lite":
                return "NOVA_LITE";
              default:
                return "NOVA_LITE"; // Default fallback
            }
          };

          const originalAiSettings = conversation.aiConfig.aiSettings || {
            model: "NOVA_LITE",
            temperature: 0.7,
            maxTokens: 1000,
          };

          // Create a mutable copy of aiSettings to avoid read-only property errors
          const aiSettings = {
            model: originalAiSettings.model ? transformModel(originalAiSettings.model) : "NOVA_LITE",
            temperature: originalAiSettings.temperature || 0.7,
            maxTokens: originalAiSettings.maxTokens || 1000,
            systemPrompt: originalAiSettings.systemPrompt || null
          };

          // Determine if AI chat should be enabled
          // If aiChatEnabled is explicitly set, use that value
          // If it's undefined but there's a content source or calendly integration, assume it should be enabled
          const shouldEnableAI = conversation.aiConfig.aiChatEnabled !== undefined
            ? conversation.aiConfig.aiChatEnabled
            : !!(conversation.aiConfig.contentSource || conversation.aiConfig.calendlyIntegration?.enabled);

          const newAiConfig = {
            aiChatEnabled: shouldEnableAI,
            contentSource: conversation.aiConfig.contentSource,
            calendlyIntegration: conversation.aiConfig.calendlyIntegration,
            aiSettings,
          };

          console.log("🤖 Setting AI configuration to:", newAiConfig);
          setAiConfiguration(newAiConfig);
        } else {
          console.log("🤖 No AI config found in conversation, keeping default state");
        }

        // selectedComponents will be populated by useEffect when assets are loaded
      }
    },
    onError: (error) => {
      console.error("❌ Error loading conversation:", error);
    },
  });

  // Debug query state
  console.log("🔍 Query state:", {
    isEditMode,
    editId,
    shouldFetchData,
    isSuperAdmin,
    clientLoading,
    selectedClient: selectedClient?.id,
    skip: !isEditMode || !editId || !shouldFetchData,
    loading: conversationLoading,
    error: conversationError,
    hasData: !!conversationData,
  });
  const [createConversation, { loading: createLoading }] = useMutation(
    CREATE_CONVERSATION,
    {
      onCompleted: (data) => {
        console.log("Conversation created:", data.createConversation);
        // Navigate to the conversations list or the created conversation
        navigate("/conversations");
      },
      onError: (error) => {
        console.error("Error creating conversation:", error);
        alert("Failed to create conversation. Please try again.");
      },
      // Backup: Refetch the conversations list if cache update fails
      refetchQueries: [
        {
          query: LIST_CONVERSATIONS,
          variables: { limit: 50 },
        },
      ],
      awaitRefetchQueries: false, // Don't wait for refetch to complete
      update: (cache, { data }) => {
        if (data?.createConversation) {
          console.log(
            "🔄 Updating cache with new conversation:",
            data.createConversation
          );

          // Update the conversations list cache
          try {
            // Try to read the existing cache
            const existingConversations = cache.readQuery<{
              listConversations?: {
                items: any[];
                nextToken?: string;
                total?: number;
              };
            }>({
              query: LIST_CONVERSATIONS,
              variables: { limit: 50 },
            });

            console.log(
              "📋 Existing conversations in cache:",
              existingConversations
            );

            if (existingConversations?.listConversations) {
              cache.writeQuery({
                query: LIST_CONVERSATIONS,
                variables: { limit: 50 },
                data: {
                  listConversations: {
                    __typename: "ConversationConnection",
                    items: [
                      data.createConversation,
                      ...existingConversations.listConversations.items,
                    ],
                    nextToken:
                      existingConversations.listConversations.nextToken,
                    total:
                      (existingConversations.listConversations.total || 0) + 1,
                  },
                },
              });
              console.log("✅ Cache updated successfully");
            } else {
              console.log(
                "📝 No existing cache found, will refetch on navigation"
              );
            }
          } catch (error) {
            console.log(
              "❌ Cache update failed, will refetch on next query:",
              error
            );

            // Alternative: Invalidate the cache to force a refetch
            cache.evict({ fieldName: "listConversations" });
            cache.gc();
            console.log("🗑️ Cache invalidated, will refetch fresh data");
          }
        }
      },
    }
  );

  const [updateConversation, { loading: updateLoading }] = useMutation(
    UPDATE_CONVERSATION,
    {
      onCompleted: (data) => {
        console.log("Conversation updated:", data.updateConversation);
        // Navigate to the conversations list or the updated conversation
        navigate("/conversations");
      },
      onError: (error) => {
        console.error("Error updating conversation:", error);
        alert("Failed to update conversation. Please try again.");
      },
      // Backup: Refetch the conversations list if cache update fails
      refetchQueries: [
        {
          query: LIST_CONVERSATIONS,
          variables: { limit: 50 },
        },
      ],
      awaitRefetchQueries: false, // Don't wait for refetch to complete
    }
  );

  const [attachAssetsToConversation, { loading: attachAssetsLoading }] =
    useMutation(ATTACH_ASSETS_TO_CONVERSATION, {
      onCompleted: (data) => {
        console.log(
          "✅ Assets attached to conversation successfully:",
          data.attachAssetsToConversation
        );
      },
      onError: (error) => {
        console.error("❌ Error attaching assets to conversation:", error);
        alert("Failed to attach assets to conversation. Please try again.");
      },
    });

  // API hooks for real data - only fetch when client is selected
  const {
    assets,
    loading: assetsLoading,
    error: assetsError,
  } = useAssets({
    autoFetch: shouldFetchData,
    limit: 100,
  });

  const {
    startMessages,
    loading: startMessagesLoading,
    error: startMessagesError,
  } = useStartMessages({
    limit: 100,
    enableSubscriptions: false, // Disable subscriptions since schema doesn't support them
  });

  const {
    destinations,
    loading: destinationsLoading,
    error: destinationsError,
  } = useDestinations({
    autoFetch: shouldFetchData,
    limit: 100,
  });

  const {
    autoResponders,
    loading: autoRespondersLoading,
    error: autoRespondersError,
  } = useAutoResponderSearch({
    limit: 100,
    initialFilters: { isActive: true },
  });

  // Debug: Monitor AI configuration changes
  useEffect(() => {
    console.log("🤖 AI Configuration state changed:", {
      aiChatEnabled: aiConfiguration.aiChatEnabled,
      hasContentSource: !!aiConfiguration.contentSource,
      contentSourceType: aiConfiguration.contentSource?.type,
      contentSourceFileId: aiConfiguration.contentSource?.fileId,
      hasCalendlyIntegration: !!aiConfiguration.calendlyIntegration,
      calendlyEnabled: aiConfiguration.calendlyIntegration?.enabled,
      calendlyLink: aiConfiguration.calendlyIntegration?.calendarLink,
      timestamp: new Date().toISOString(),
    });
  }, [aiConfiguration]);

  // SIMPLIFIED: Load conversation components for editing
  useEffect(() => {
    if (isEditMode && conversationData?.getConversation) {
      const conversation = conversationData.getConversation;
      console.log(
        "🔍 SIMPLIFIED: Loading conversation for edit:",
        conversation
      );

      const components: SelectedComponent[] = [];

      // Add start message (take first one only)
      if (conversation.startMessages?.length > 0) {
        const startMessage = conversation.startMessages[0];

        components.push({
          id: startMessage.messageId,
          type: "messageTemplate",
          name: startMessage.name,
          content: startMessage.content || "",
          description: startMessage.content || "",
          templateType: startMessage.type as
            | "SMS"
            | "CHAT"
            | "EMAIL"
            | "GENERAL",
        });
      }

      // Add auto-responder
      if (conversation.autoResponder) {
        components.push({
          id: conversation.autoResponder.id,
          type: "autoResponder",
          name: conversation.autoResponder.name,
          content: conversation.autoResponder.content || "",
          description: conversation.autoResponder.content || "",
        });
      }

      // Add destination (take first one only)
      if (conversation.destinations?.length > 0) {
        const destination = conversation.destinations[0];

        components.push({
          id: destination.destinationId,
          type: "destination",
          name: destination.name,
          url: destination.url,
          description: destination.url,
        });
      }

      // Add assets (simplified)
      if (conversation.assets?.length > 0) {
        conversation.assets.forEach((asset: any) => {
          components.push({
            id: asset.fileId || asset.id,
            type: "asset",
            name: asset.name || asset.fileId || asset.id,
            description: `${asset.type} asset`,
            assetType: asset.type,
          });
        });
      }

      setSelectedComponents(components);
    }
  }, [isEditMode, conversationData]);

  // Separate effect to update asset components when assets data becomes available
  useEffect(() => {
    if (isEditMode && conversationData?.getConversation && assets.length > 0) {
      const conversation = conversationData.getConversation;

      // Update existing asset components with full asset data
      setSelectedComponents((prevComponents) => {
        const updatedComponents = prevComponents.map((component) => {
          if (component.type === "asset") {
            const fullAsset = assets.find((asset) => asset.id === component.id);
            if (fullAsset && component.name === component.id) {
              // Only update if we're still using the ID as name (temporary state)
              return {
                ...component,
                name: fullAsset.name,
                description:
                  (fullAsset as any).description || `${fullAsset.type} asset`,
                assetType: fullAsset.type as any,
              };
            }
          }
          return component;
        });

        // Only update if there were actual changes
        const hasChanges = updatedComponents.some(
          (comp, index) => comp !== prevComponents[index]
        );

        if (hasChanges) {
          console.log("🔄 Updated asset components with full data");
          return updatedComponents;
        }

        return prevComponents;
      });
    }
  }, [isEditMode, conversationData, assets]);

  const addComponent = (component: SelectedComponent) => {
    // For messageTemplate, auto-responder, and destination, only allow one selection
    if (
      component.type === "messageTemplate" ||
      component.type === "autoResponder" ||
      component.type === "destination"
    ) {
      setSelectedComponents((prev) =>
        prev.filter((c) => c.type !== component.type).concat(component)
      );
    } else {
      // Check if component is already added
      if (!selectedComponents.find((c) => c.id === component.id)) {
        setSelectedComponents((prev) => [...prev, component]);
      }
    }
  };

  const removeComponent = (componentId: string) => {
    setSelectedComponents((prev) => prev.filter((c) => c.id !== componentId));
  };

  // Transform API data to component format with safety checks
  const transformedComponents = {
    asset: (assets || []).map((asset) => ({
      id: asset.id,
      name: asset.name || "Unnamed Asset",
      description: `${asset.type?.toUpperCase() || "UNKNOWN"} • ${
        asset.files?.[0]?.size
          ? `${(asset.files[0].size / 1024 / 1024).toFixed(1)} MB`
          : "Unknown size"
      }`,
      assetType:
        (asset.type?.toLowerCase() as "pdf" | "image" | "video" | "url") ||
        "pdf",
    })),
    messageTemplate: (startMessages || []).map((message) => ({
      id: message.id,
      name: message.name || "Unnamed Message",
      description:
        (message.content || "").substring(0, 100) +
        ((message.content || "").length > 100 ? "..." : ""),
      templateType: (message as any).messageType || "SMS",
      content: message.content || "",
    })),
    autoResponder: (autoResponders || []).map((responder) => ({
      id: responder.id,
      name: responder.name || "Unnamed Auto-Responder",
      description:
        (responder.content || "").substring(0, 100) +
        ((responder.content || "").length > 100 ? "..." : ""),
    })),
    destination: (destinations || []).map((destination) => ({
      id: destination.id,
      name: destination.name || "Unnamed Destination",
      description: destination.description || "No description",
      url: destination.url || "",
      destinationType: (destination as any).type?.toLowerCase() || "webpage",
    })),
  };

  const handleAssetSelection = (assetId: string, checked: boolean) => {
    if (checked) {
      setSelectedAssets((prev) => [...prev, assetId]);
    } else {
      setSelectedAssets((prev) => prev.filter((id) => id !== assetId));
    }
  };

  const handleSelectAllAssets = (checked: boolean) => {
    if (checked) {
      const filteredAssets = filterAssets(
        transformedComponents.asset,
        assetSearchQuery,
        statusFilter
      );
      const allAssetIds = filteredAssets.map((asset) => asset.id);
      setSelectedAssets(allAssetIds);
    } else {
      setSelectedAssets([]);
    }
  };

  const handleBulkAddAssets = () => {
    const assetsToAdd = transformedComponents.asset
      .filter((asset) => selectedAssets.includes(asset.id))
      .map((asset) => ({
        ...asset,
        type: "asset" as const,
      }));

    // Remove existing assets and add new ones
    setSelectedComponents((prev) => [
      ...prev.filter((c) => c.type !== "asset"),
      ...assetsToAdd,
    ]);

    setSelectedAssets([]);
  };

  const filterAssets = (
    assets: any[],
    query: string,
    status: "all" | "available" | "added" = "all"
  ) => {
    let filteredAssets = assets;

    // Filter by status
    if (status === "available") {
      filteredAssets = assets.filter(
        (asset) => !selectedComponents.some((c) => c.id === asset.id)
      );
    } else if (status === "added") {
      filteredAssets = assets.filter((asset) =>
        selectedComponents.some((c) => c.id === asset.id)
      );
    }

    // Filter by search query
    if (!query.trim()) return filteredAssets;
    const searchText = query.toLowerCase();
    return filteredAssets.filter(
      (asset) =>
        asset.name.toLowerCase().includes(searchText) ||
        asset.description?.toLowerCase().includes(searchText) ||
        asset.assetType?.toLowerCase().includes(searchText)
    );
  };

  const filterComponents = (components: any[], query: string) => {
    if (!query.trim()) return components;

    return components.filter((component) => {
      const searchText = query.toLowerCase();
      return (
        component.name.toLowerCase().includes(searchText) ||
        component.description?.toLowerCase().includes(searchText) ||
        component.assetType?.toLowerCase().includes(searchText) ||
        component.destinationType?.toLowerCase().includes(searchText) ||
        component.url?.toLowerCase().includes(searchText)
      );
    });
  };

  // DEBUG: Log filtering for messageTemplate specifically
  if (activeTab === "messageTemplate") {
    const filteredComponents = filterComponents(
      transformedComponents.messageTemplate,
      searchQuery
    );
    console.log("🎯 FILTERING messageTemplate:", {
      activeTab,
      searchQuery,
      rawComponents: transformedComponents.messageTemplate,
      filteredComponents,
      rawLength: transformedComponents.messageTemplate.length,
      filteredLength: filteredComponents.length,
    });
  }

  // Handler functions for creating/updating conversations
  const handleCreateConversation = async () => {
    if (!conversationName.trim() || selectedComponents.length === 0) {
      alert(
        "Please provide a conversation name and select at least one component."
      );
      return;
    }

    // Validate AI configuration if enabled
    if (aiConfiguration.aiChatEnabled) {
      if (
        !aiConfiguration.contentSource ||
        (aiConfiguration.contentSource.type === "DESTINATION_URL" &&
          !aiConfiguration.contentSource.destinationId) ||
        (aiConfiguration.contentSource.type === "UPLOADED_FILE" &&
          !aiConfiguration.contentSource.fileId)
      ) {
        alert("Please configure a content source for AI chat.");
        return;
      }

      if (aiConfiguration.calendlyIntegration?.enabled) {
        if (!aiConfiguration.calendlyIntegration.calendarLink) {
          alert("Please provide a Calendly calendar link.");
          return;
        }
      }
    }

    if (createLoading || updateLoading || attachAssetsLoading) return; // Prevent double submission

    try {
      // Find the selected auto-responder
      const autoResponderComponent = selectedComponents.find(
        (c) => c.type === "autoResponder"
      );

      // Prepare the input for the mutation
      const baseInput = {
        name: conversationName.trim(),
        description: conversationDescription.trim() || undefined,
        status: "ACTIVE",
        targetAudience: {
          segments: [],
          criteria: JSON.stringify({}),
        },
        assets: selectedComponents
          .filter((c) => c.type === "asset")
          .map((asset) => ({
            id: asset.id,
            type: asset.assetType || "pdf",
            fileId: asset.id,
            trackingEnabled: true,
            qrCodeEnabled: true,
          })),
        startMessages: selectedComponents
          .filter((c) => c.type === "messageTemplate")
          .map((startMessage) => ({
            id: startMessage.id,
            messageId: startMessage.id,
            name: startMessage.name,
            content: startMessage.content || "",
            type: startMessage.templateType || "SMS",
            isActive: true,
          })),
        destinations: selectedComponents
          .filter((c) => c.type === "destination")
          .map((destination) => ({
            id: destination.id,
            destinationId: destination.id,
            name: destination.name,
            url: destination.url || "",
            type: (destination as any).destinationType || "URL",
            isActive: true,
          })),
        autoResponderId: autoResponderComponent?.id || undefined,
        // AI Chat Configuration - always send to save the enabled/disabled state
        aiConfig: {
          aiChatEnabled: aiConfiguration.aiChatEnabled,
          contentSource: aiConfiguration.aiChatEnabled
            ? cleanContentSource(aiConfiguration.contentSource)
            : undefined,
          calendlyIntegration: cleanCalendlyIntegration(
            aiConfiguration.calendlyIntegration
          ),
          aiSettings: aiConfiguration.aiSettings
            ? {
                model: aiConfiguration.aiSettings.model,
                temperature: aiConfiguration.aiSettings.temperature,
                maxTokens: aiConfiguration.aiSettings.maxTokens,
                ...(aiConfiguration.aiSettings.systemPrompt && {
                  systemPrompt: aiConfiguration.aiSettings.systemPrompt,
                }),
              }
            : undefined,
        },
      };

      if (isEditMode && editId) {
        // Update existing conversation
        const updateInput = {
          id: editId,
          ...baseInput,
        };
        console.log("Updating conversation with input:", updateInput);
        console.log(
          "🔍 DEBUG UPDATE: aiSettings being sent:",
          JSON.stringify(updateInput.aiConfig?.aiSettings, null, 2)
        );
        console.log(
          "🔍 DEBUG UPDATE: aiSettings keys:",
          updateInput.aiConfig?.aiSettings
            ? Object.keys(updateInput.aiConfig.aiSettings)
            : "null"
        );
        await updateConversation({ variables: { input: updateInput } });

        // Attach assets to conversation if any are selected
        const assetComponents = selectedComponents.filter(
          (component) => component.type === "asset"
        );
        if (assetComponents.length > 0) {
          const assetIds = assetComponents.map((component) => component.id);
          console.log("🔗 Attaching assets to conversation:", {
            conversationId: editId,
            assetIds,
          });
          await attachAssetsToConversation({
            variables: {
              conversationId: editId,
              assetIds,
            },
          });
        }
      } else {
        // Create new conversation
        const createInput = {
          ...baseInput,
          startDate: new Date().toISOString(),
        };
        console.log("Creating conversation with input:", createInput);
        await createConversation({ variables: { input: createInput } });
      }
    } catch (error) {
      console.error("Error in handleCreateConversation:", error);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!conversationName.trim()) {
      alert("Please provide a conversation name to save as draft.");
      return;
    }

    if (createLoading) return; // Prevent double submission

    try {
      // Find the selected auto-responder
      const autoResponderComponent = selectedComponents.find(
        (c) => c.type === "autoResponder"
      );

      // Prepare the input for the mutation with DRAFT status
      const input = {
        name: conversationName.trim(),
        description: conversationDescription.trim() || undefined,
        status: "DRAFT",
        startDate: new Date().toISOString(),
        targetAudience: {
          segments: [],
          criteria: JSON.stringify({}),
        },
        assets: selectedComponents
          .filter((c) => c.type === "asset")
          .map((asset) => ({
            id: asset.id,
            type: asset.assetType || "pdf",
            fileId: asset.id,
            trackingEnabled: true,
            qrCodeEnabled: true,
          })),
        startMessages: selectedComponents
          .filter((c) => c.type === "messageTemplate")
          .map((startMessage) => ({
            id: startMessage.id,
            messageId: startMessage.id,
            name: startMessage.name,
            content: startMessage.content || "",
            type: startMessage.templateType || "SMS",
            isActive: true,
          })),
        destinations: selectedComponents
          .filter((c) => c.type === "destination")
          .map((destination) => ({
            id: destination.id,
            destinationId: destination.id,
            name: destination.name,
            url: destination.url || "",
            type: (destination as any).destinationType || "URL",
            isActive: true,
          })),
        autoResponderId: autoResponderComponent?.id || undefined,
        // AI Chat Configuration - always send to save the enabled/disabled state
        aiConfig: {
          aiChatEnabled: aiConfiguration.aiChatEnabled,
          contentSource: aiConfiguration.aiChatEnabled
            ? cleanContentSource(aiConfiguration.contentSource)
            : undefined,
          calendlyIntegration: cleanCalendlyIntegration(
            aiConfiguration.calendlyIntegration
          ),
          aiSettings: aiConfiguration.aiSettings
            ? {
                model: aiConfiguration.aiSettings.model,
                temperature: aiConfiguration.aiSettings.temperature,
                maxTokens: aiConfiguration.aiSettings.maxTokens,
                ...(aiConfiguration.aiSettings.systemPrompt && {
                  systemPrompt: aiConfiguration.aiSettings.systemPrompt,
                }),
              }
            : undefined,
        },
      };

      console.log("Saving conversation as draft with input:", input);
      console.log(
        "🔍 DEBUG: aiSettings being sent:",
        JSON.stringify(input.aiConfig?.aiSettings, null, 2)
      );
      console.log(
        "🔍 DEBUG: aiSettings keys:",
        input.aiConfig?.aiSettings
          ? Object.keys(input.aiConfig.aiSettings)
          : "null"
      );
      await createConversation({ variables: { input } });
    } catch (error) {
      console.error("Error in handleSaveAsDraft:", error);
    }
  };

  // Loading and error states
  const isLoading =
    clientLoading ||
    assetsLoading ||
    startMessagesLoading ||
    destinationsLoading ||
    autoRespondersLoading;
  const hasError =
    assetsError ||
    startMessagesError ||
    destinationsError ||
    autoRespondersError;

  const getComponentIcon = (type: string) => {
    const icons = {
      asset: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      messageTemplate: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
          />
        </svg>
      ),
      autoResponder: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      ),
      destination: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      ),
    };
    return icons[type as keyof typeof icons];
  };

  const getComponentColor = (type: string) => {
    const colors = {
      asset: "purple",
      messageTemplate: "blue",
      autoResponder: "green",
      destination: "orange",
    };
    return colors[type as keyof typeof colors];
  };

  // Show loading state when fetching conversation data for editing
  if (isEditMode && conversationLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading conversation data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditMode ? "Edit Conversation" : "Create New Conversation"}
            </h1>
            <p className="mt-2 text-gray-600">
              Build your conversation by selecting and attaching reusable
              components
            </p>
          </div>
          <Link
            to="/conversations"
            className="text-gray-500 hover:text-gray-700 font-medium"
          >
            ← Back to Conversations
          </Link>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">Loading components...</span>
          </div>
        </div>
      )}

      {/* Client Selection Required */}
      {!clientLoading && !selectedClient && isSuperAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Client Selection Required
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Please select a client from the dropdown in the header to
                  create conversations.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {hasError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading components
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  There was an error loading the component data. Please try
                  refreshing the page.
                </p>
                {assetsError && (
                  <p>
                    Assets:{" "}
                    {(assetsError as any).message || String(assetsError)}
                  </p>
                )}
                {startMessagesError && (
                  <p>
                    Start Messages:{" "}
                    {(startMessagesError as any).message ||
                      String(startMessagesError)}
                  </p>
                )}
                {destinationsError && (
                  <p>
                    Destinations:{" "}
                    {destinationsError.message || String(destinationsError)}
                  </p>
                )}
                {autoRespondersError && (
                  <p>
                    Auto-Responders:{" "}
                    {autoRespondersError.message || String(autoRespondersError)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isLoading && !hasError && selectedClient && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversation Details */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow border p-6 sticky top-6">
              {/* <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Conversation Details
              </h2> */}

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="conversation-name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Conversation Name
                  </label>
                  <input
                    type="text"
                    id="conversation-name"
                    value={conversationName}
                    onChange={(e) => setConversationName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Q1 Product Launch"
                    data-testid="conversation-name-input"
                  />
                </div>

                <div>
                  <label
                    htmlFor="conversation-description"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Description
                  </label>
                  <textarea
                    id="conversation-description"
                    value={conversationDescription}
                    onChange={(e) => setConversationDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Brief description of your conversation goals"
                    data-testid="conversation-description-input"
                  />
                </div>

                {/* AI Chat Configuration */}
                <div className="border-t border-gray-200 pt-4">
                  <AIChatConfiguration
                    key={`ai-config-${editId || 'new'}-${JSON.stringify(aiConfiguration)}`}
                    configuration={aiConfiguration}
                    onChange={setAiConfiguration}
                    availableDestinations={destinations.map((dest) => ({
                      id: dest.id,
                      name: dest.name,
                      url: dest.url,
                    }))}
                    disabled={
                      createLoading || updateLoading || attachAssetsLoading
                    }
                  />
                </div>

                {/* LinkedIn Conversion Tracking */}
                {editId && (
                  <div className="border-t border-gray-200 pt-4">
                    <LinkedInConversionConfiguration
                      conversationId={editId}
                      onConfigurationChange={() => {
                        console.log("LinkedIn conversion configuration updated");
                      }}
                    />
                  </div>
                )}

                {/* Conversation Preview */}
                <div className="mb-6">
                  {/* <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Conversation Preview
                  </h3> */}
                  {(() => {
                    // DEBUG: Log what we're passing to ConversationPreview
                    const startMessage = selectedComponents.find(
                      (c) => c.type === "messageTemplate"
                    )?.content;
                    const autoResponderMessage = selectedComponents.find(
                      (c) => c.type === "autoResponder"
                    )?.content;
                    const destinationComponent = selectedComponents.find(
                      (c) => c.type === "destination"
                    );
                    const destinationInfo = destinationComponent
                      ? {
                          name: destinationComponent.name || "",
                          url: destinationComponent.url || "",
                        }
                      : undefined;

                    return (
                      <ConversationPreview
                        startMessage={startMessage}
                        autoResponderMessage={autoResponderMessage}
                        destinationInfo={destinationInfo}
                        aiChatEnabled={aiConfiguration.aiChatEnabled}
                        aiContentSource={aiConfiguration.contentSource}
                        calendlyEnabled={
                          aiConfiguration.calendlyIntegration?.enabled
                        }
                      />
                    );
                  })()}
                </div>

                {/* Selected Components */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Selected Components
                  </h3>
                  {selectedComponents.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">
                      No components selected yet
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {selectedComponents.map((component) => {
                        const color = getComponentColor(component.type);
                        return (
                          <div key={component.id} className="space-y-2">
                            <div
                              className={`flex items-center justify-between p-2 bg-${color}-50 border border-${color}-200 rounded-lg`}
                            >
                              <div className="flex items-center space-x-2">
                                <div className={`text-${color}-600`}>
                                  {getComponentIcon(component.type)}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {component.name}
                                  </p>
                                  <p className="text-xs text-gray-500 capitalize">
                                    {component.type}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => removeComponent(component.id)}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="pt-4 space-y-3">
                  <button
                    onClick={handleCreateConversation}
                    disabled={
                      !conversationName ||
                      selectedComponents.length === 0 ||
                      createLoading ||
                      updateLoading ||
                      attachAssetsLoading
                    }
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    data-testid="create-conversation-button"
                  >
                    {createLoading || updateLoading || attachAssetsLoading
                      ? isEditMode
                        ? attachAssetsLoading
                          ? "Attaching Assets..."
                          : "Updating..."
                        : "Creating..."
                      : isEditMode
                      ? "Update Conversation"
                      : "Create Conversation"}
                  </button>
                  {!isEditMode && (
                    <button
                      onClick={handleSaveAsDraft}
                      disabled={!conversationName || createLoading}
                      className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      {createLoading ? "Saving..." : "Save as Draft"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Component Selection */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Select Components
                </h2>
                <p className="text-sm text-gray-600">
                  Choose reusable components to attach to your conversation
                </p>
              </div>

              {/* Component Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                  {[
                    { key: "asset", label: "Assets", singleSelect: false },
                    {
                      key: "messageTemplate",
                      label: "Start Message",
                      singleSelect: true,
                    },
                    {
                      key: "autoResponder",
                      label: "Auto-Responders",
                      singleSelect: true,
                    },
                    {
                      key: "destination",
                      label: "Destinations",
                      singleSelect: true,
                    },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key as any)}
                      className={`${
                        activeTab === tab.key
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                      data-testid={`conversation-tab-${tab.key}`}
                    >
                      <span>{tab.label}</span>
                      {tab.singleSelect && (
                        <span className="text-gray-400 text-xs">(1 max)</span>
                      )}
                      {selectedComponents.filter((c) => c.type === tab.key)
                        .length > 0 && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {
                            selectedComponents.filter((c) => c.type === tab.key)
                              .length
                          }
                        </span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Component Grid */}
              <div className="p-6">
                {/* Search Input */}
                <div className="mb-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder={`Search ${activeTab
                        .replace(/([A-Z])/g, " $1")
                        .toLowerCase()}s...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      data-testid="conversation-component-search"
                    />
                  </div>
                </div>

                {/* Assets Table View */}
                {activeTab === "asset" ? (
                  <div className="space-y-4">
                    {/* Bulk Actions Bar */}
                    {selectedAssets.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <span className="text-sm font-medium text-blue-900">
                              {selectedAssets.length} asset
                              {selectedAssets.length !== 1 ? "s" : ""} selected
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={handleBulkAddAssets}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                              data-testid="assets-bulk-add"
                            >
                              Add to Conversation
                            </button>
                            <button
                              onClick={() => setSelectedAssets([])}
                              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                              data-testid="assets-bulk-clear"
                            >
                              Clear Selection
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Assets Table */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <input
                              type="checkbox"
                              checked={
                                selectedAssets.length ===
                                  filterAssets(
                                    transformedComponents.asset,
                                    assetSearchQuery,
                                    statusFilter
                                  ).length &&
                                filterAssets(
                                  transformedComponents.asset,
                                  assetSearchQuery,
                                  statusFilter
                                ).length > 0
                              }
                              onChange={(e) =>
                                handleSelectAllAssets(e.target.checked)
                              }
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-900">
                              Select All (
                              {
                                filterAssets(
                                  transformedComponents.asset,
                                  assetSearchQuery,
                                  statusFilter
                                ).length
                              }{" "}
                              assets)
                            </span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <select
                              value={statusFilter}
                              onChange={(e) =>
                                setStatusFilter(
                                  e.target.value as
                                    | "all"
                                    | "available"
                                    | "added"
                                )
                              }
                              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="all">All Assets</option>
                              <option value="available">Available</option>
                              <option value="added">
                                Added to Conversation
                              </option>
                            </select>
                            <div className="relative">
                              <input
                                type="text"
                                placeholder="Search assets..."
                                value={assetSearchQuery}
                                data-testid="assets-search"
                                onChange={(e) =>
                                  setAssetSearchQuery(e.target.value)
                                }
                                className="block w-64 pl-3 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="max-h-96 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Select
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Asset
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filterAssets(
                              transformedComponents.asset,
                              assetSearchQuery,
                              statusFilter
                            ).map((asset) => {
                              const isSelected = selectedAssets.includes(
                                asset.id
                              );
                              const isInConversation = selectedComponents.some(
                                (c) => c.id === asset.id
                              );

                              return (
                                <tr
                                  key={asset.id}
                                  className={`hover:bg-gray-50 ${
                                    isSelected ? "bg-blue-50" : ""
                                  }`}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) =>
                                        handleAssetSelection(
                                          asset.id,
                                          e.target.checked
                                        )
                                      }
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0 h-8 w-8">
                                        <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                          {getComponentIcon("asset")}
                                        </div>
                                      </div>
                                      <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">
                                          {asset.name}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                          {asset.description}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                        asset.assetType === "pdf"
                                          ? "bg-red-100 text-red-800"
                                          : asset.assetType === "image"
                                          ? "bg-green-100 text-green-800"
                                          : asset.assetType === "video"
                                          ? "bg-blue-100 text-blue-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {asset.assetType.toUpperCase()}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {isInConversation ? (
                                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                        Added to Conversation
                                      </span>
                                    ) : (
                                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                                        Available
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : activeTab === "autoResponder" ? (
                  /* Auto-Responder Selector */
                  <AutoResponderSelector
                    selectedAutoResponderId={selectedAutoResponderId}
                    onAutoResponderChange={(autoResponderId) => {
                      setSelectedAutoResponderId(autoResponderId);
                      // Update selected components
                      if (autoResponderId) {
                        // Find the actual auto-responder data
                        const selectedAutoResponder = autoResponders?.find(
                          (ar) => ar.id === autoResponderId
                        );
                        if (selectedAutoResponder) {
                          const autoResponderComponent = {
                            id: autoResponderId,
                            type: "autoResponder" as const,
                            name: selectedAutoResponder.name,
                            description: selectedAutoResponder.content,
                            content: selectedAutoResponder.content,
                          };
                          setSelectedComponents((prev) =>
                            prev
                              .filter((c) => c.type !== "autoResponder")
                              .concat(autoResponderComponent)
                          );
                        }
                      } else {
                        // Remove auto-responder from selected components
                        setSelectedComponents((prev) =>
                          prev.filter((c) => c.type !== "autoResponder")
                        );
                      }
                    }}
                    showPreview={false}
                  />
                ) : (
                  /* Other Components Grid View */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filterComponents(
                      transformedComponents[activeTab],
                      searchQuery
                    ).map((component) => {
                      const isSelected = selectedComponents.find(
                        (c) => c.id === component.id
                      );
                      const color = getComponentColor(activeTab);
                      const isSingleSelectType =
                        (activeTab as string) === "messageTemplate" ||
                        (activeTab as string) === "autoResponder" ||
                        (activeTab as string) === "destination";
                      const hasSelectionInType = selectedComponents.some(
                        (c) => c.type === activeTab
                      );
                      const canSelect =
                        !isSingleSelectType ||
                        !hasSelectionInType ||
                        isSelected;

                      return (
                        <div
                          key={component.id}
                          className="space-y-3"
                          data-testid={`component-card-${activeTab}-${component.id}`}
                        >
                          <div
                            className={`border-2 rounded-lg p-4 transition-all ${
                              !canSelect
                                ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-50"
                                : isSelected
                                ? `border-${color}-500 bg-${color}-50 cursor-pointer`
                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer"
                            }`}
                            onClick={(e) => {
                              // Don't trigger if clicking on radio button
                              if (
                                e.target instanceof HTMLInputElement &&
                                e.target.type === "radio"
                              ) {
                                return;
                              }
                              if (canSelect) {
                                if (isSingleSelectType) {
                                  // For single-select types, toggle selection
                                  if (isSelected) {
                                    // Deselect if already selected
                                    removeComponent(component.id);
                                  } else {
                                    // Select this component
                                    addComponent({
                                      id: component.id,
                                      type: activeTab,
                                      name: component.name,
                                      description: component.description,
                                      content: component.content,
                                      ...(activeTab === "destination" && {
                                        url: component.url,
                                      }),
                                    });
                                  }
                                } else {
                                  // For multi-select types, use existing logic
                                  addComponent({
                                    id: component.id,
                                    type: activeTab,
                                    name: component.name,
                                    description: component.description,
                                    content: component.content,
                                    ...(activeTab === "destination" && {
                                      url: component.url,
                                    }),
                                  });
                                }
                              }
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-3 flex-1">
                                {/* Radio button for single-select types, icon for multi-select */}
                                {isSingleSelectType ? (
                                  <div className="mt-1">
                                    <input
                                      type="radio"
                                      name={`${activeTab}-selection`}
                                      checked={!!isSelected}
                                      onChange={() => {
                                        if (canSelect) {
                                          if (isSelected) {
                                            // Deselect if already selected
                                            removeComponent(component.id);
                                          } else {
                                            // Select this component
                                            addComponent({
                                              id: component.id,
                                              type: activeTab,
                                              name: component.name,
                                              description:
                                                component.description,
                                              content: component.content,
                                              ...(activeTab ===
                                                "destination" && {
                                                url: component.url,
                                              }),
                                            });
                                          }
                                        }
                                      }}
                                      className="text-blue-600 focus:ring-blue-500"
                                      disabled={!canSelect}
                                    />
                                  </div>
                                ) : (
                                  <div
                                    className={`${
                                      isSelected
                                        ? `text-${color}-600`
                                        : "text-gray-400"
                                    } mt-1`}
                                  >
                                    {getComponentIcon(activeTab)}
                                  </div>
                                )}
                                <div className="flex-1">
                                  <h3 className="font-medium text-gray-900">
                                    {component.name}
                                  </h3>
                                  {component.description && (
                                    <p className="text-sm text-gray-500 mt-1">
                                      {component.description}
                                    </p>
                                  )}
                                  <div className="flex items-center mt-2 space-x-4 text-xs text-gray-500">
                                    {/* <span className="capitalize">
                                      {activeTab
                                        .replace(/([A-Z])/g, " $1")
                                        .trim()}
                                    </span> */}

                                    {activeTab === "messageTemplate" && (
                                      <span>Used in 3 conversations</span>
                                    )}
                                    {activeTab === "messageTemplate" && (
                                      <span>Used 45 times</span>
                                    )}
                                    {(activeTab as string) ===
                                      "autoResponder" && (
                                      <span>68% response rate</span>
                                    )}
                                    {activeTab === "destination" && (
                                      <>
                                        <span className="capitalize">
                                          {component.destinationType}
                                        </span>
                                        <span
                                          title={component.url}
                                          className="text-blue-600 font-mono text-xs"
                                        >
                                          {component.url?.substring(0, 26)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  {!canSelect && isSingleSelectType && (
                                    <p className="text-xs text-orange-600 mt-1">
                                      Only one{" "}
                                      {activeTab
                                        .replace(/([A-Z])/g, " $1")
                                        .toLowerCase()}{" "}
                                      can be selected
                                    </p>
                                  )}
                                </div>
                              </div>
                              {isSelected && (
                                <div className={`text-${color}-600`}>
                                  <svg
                                    className="w-5 h-5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* No Results Message */}
                {filterComponents(transformedComponents[activeTab], searchQuery)
                  .length === 0 &&
                  searchQuery.trim() && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No results found
                      </h3>
                      <p className="text-gray-500 mb-4">
                        No {activeTab.replace(/([A-Z])/g, " $1").toLowerCase()}s
                        match your search for "{searchQuery}"
                      </p>
                      <button
                        onClick={() => setSearchQuery("")}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Clear search
                      </button>
                    </div>
                  )}

                {/* Create New Component */}
                {/* <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-3">
                      Don't see what you need? Create a new{" "}
                      {activeTab.replace(/([A-Z])/g, " $1").toLowerCase()}{" "}
                      component.
                    </p>
                    <button
                      className={`${
                        activeTab === "asset"
                          ? "bg-purple-600 hover:bg-purple-700"
                          : activeTab === "messageTemplate"
                          ? "bg-blue-600 hover:bg-blue-700"
                          : activeTab === "autoResponder"
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-orange-600 hover:bg-orange-700"
                      } text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium`}
                    >
                      Create New{" "}
                      {activeTab.charAt(0).toUpperCase() +
                        activeTab.slice(1).replace(/([A-Z])/g, " $1")}
                    </button>
                  </div>
                </div> */}
              </div>
              {/* Conversation Preview */}
              {selectedComponents.length > 0 && (
                <div className="bg-white rounded-lg shadow border mt-5">
                  <div className="p-6">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="font-medium text-gray-900 mb-4">
                        User Journey Flow
                      </h3>
                      <div className="flex items-center space-x-4 overflow-x-auto">
                        {selectedComponents
                          .sort((a, b) => {
                            const order = [
                              "asset",
                              "messageTemplate",
                              "autoResponder",
                            ];
                            return (
                              order.indexOf(a.type) - order.indexOf(b.type)
                            );
                          })
                          .map((component, index) => {
                            const color = getComponentColor(component.type);
                            return (
                              <React.Fragment key={component.id}>
                                <div className="flex flex-col items-center min-w-0 flex-shrink-0">
                                  <div
                                    className={`w-12 h-12 bg-${color}-100 border-2 border-${color}-300 rounded-lg flex items-center justify-center text-${color}-600 mb-2`}
                                  >
                                    {getComponentIcon(component.type)}
                                  </div>
                                  <p className="text-xs font-medium text-gray-900 text-center">
                                    {component.name}
                                  </p>
                                  <p className="text-xs text-gray-500 capitalize">
                                    {component.type}
                                  </p>
                                </div>
                                {index < selectedComponents.length - 1 && (
                                  <div className="flex-shrink-0">
                                    <svg
                                      className="w-6 h-6 text-gray-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
